import t from '@rcm/i18n-client';
import {
  InvisibleReCaptcha2Button,
  useCaptchaActionEnabled,
} from '@rcm/recaptcha-2';
import type { FormEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import React, { useCallback, useState } from 'react';
import { CHECKOUT_CREDIT_CARD_STANDARD } from '@reliv/cart';
import getCreditCardData from '../../functions/getCreditCardData';
import useCreditCardFieldsState from '../../hooks/useCreditCardFieldsState';
import type { CreditCardData, LegacyCartFormMessage } from '../../types';
import CreditCardFields from '../credit-card/CreditCardFields';
import CreditCardForm from '../credit-card/CreditCardForm';
import {
  selectCartPaymentInfo,
  selectCartInfo,
  selectIsEditing,
} from 'cart-ui-react/src/autoshipState/selectors';
import CartV2CreditCardFields from '/cart-ui-react/src/container/CartV2/components/CartV2CreditCardFields';
import { getCheckoutOrderManager } from 'cart-ui';
import {
  setEditingAlert,
  setSnackbar,
} from 'cart-ui-react/src/autoshipState/autoshipSlice';

interface Props {
  order: any;
  onSubmit?: (data: {
    type: string;
    data: CreditCardData & { captcha?: string };
  }) => void;
  messages?: LegacyCartFormMessage[];
  paymentType: string;
  disabled?: boolean;
  isCartV2?: boolean;
  setError?: any;
}

const PaymentFormCreditCard = ({
  onSubmit,
  paymentType,
  disabled = false,
  order,
  messages = [],
  isCartV2 = false,
  setError,
}: Props) => {
  const reCaptcha2Enabled = useCaptchaActionEnabled(
    CHECKOUT_CREDIT_CARD_STANDARD
  );
  const cartV2PaymentInfo = useSelector(selectCartPaymentInfo);
  const cartV2Info = useSelector(selectCartInfo);
  const creditCardFieldsState = useCreditCardFieldsState();
  const paymentInfo = isCartV2
    ? cartV2PaymentInfo
    : getCreditCardData(creditCardFieldsState);
  const [captcha, setCaptcha] = useState('');
  const dispatch = useDispatch();
  const isEditing = useSelector(selectIsEditing);

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      if (window?.grecaptcha?.getResponse() != '') {
        dispatch(
          setSnackbar({
            message: t(
              'Repeated save attempts disabled by reCAPTCHA, please wait a few moments before saving.'
            ),
            type: 'error',
          })
        );
      }
      onSubmit({
        type: paymentType,
        data: {
          ...paymentInfo,
          ...(reCaptcha2Enabled ? { captcha } : {}),
        },
      });
    },
    [captcha, onSubmit, paymentInfo, paymentType, reCaptcha2Enabled]
  );

  const handleEditingAlert = () => {
    if (isCartV2 && isEditing) {
      dispatch(
        setSnackbar({
          message: t('Please save any changes before you proceed.'),
          type: 'warning',
          delay: 4500,
        })
      );
      dispatch(setEditingAlert(true));
      setTimeout(() => {
        dispatch(setEditingAlert(false));
      }, 100);
      return;
    }
  };

  const handleSubmitV2 = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      if (disabled || isEditing) return;

      getCheckoutOrderManager().then(async (orderManager) => {
        const payment = {
          cardNumber: paymentInfo.cardNumber,
          nameOnCard: paymentInfo.nameOnCard,
          expirationMonth: Number(paymentInfo.expirationMonth),
          expirationYear: Number(paymentInfo.expirationYear),
          verificationCode: paymentInfo.verificationCode,
        };
        const data = {
          ...(reCaptcha2Enabled ? { captcha } : {}),
          ...payment,
        };
        // check for cart changes (mainly Kalogris donations but any frontend cart changes would get applied here)
        await orderManager.patchOrder(cartV2Info);
        await orderManager
          .processCurrentOrder({ type: paymentType, data: data })
          .then(() => {
            const paymentError = JSON.parse(
              localStorage.getItem('paymentError')
            );
            if (paymentError) {
              dispatch(setError(paymentError[0].value));
              localStorage.removeItem('paymentError');
            }
          });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [captcha, paymentType, reCaptcha2Enabled]
  );
  return (
    <CreditCardForm
      onSubmit={isCartV2 ? handleSubmitV2 : handleSubmit}
      cartV2={isCartV2}
    >
      <CreditCardForm.Body fullwidth>
        {isCartV2 ? (
          <CartV2CreditCardFields />
        ) : (
          <CreditCardFields
            {...creditCardFieldsState}
            disabled={disabled}
            messages={messages}
            order={order}
          />
        )}
      </CreditCardForm.Body>
      <CreditCardForm.Foot>
        <div onClick={handleEditingAlert}>
          <div
            style={
              disabled || isEditing
                ? { pointerEvents: 'none', opacity: 0.7 }
                : {}
            }
          >
            <InvisibleReCaptcha2Button
              action={CHECKOUT_CREDIT_CARD_STANDARD}
              btn='primary'
              className='continueButton'
              disabled={disabled}
              onValueChange={setCaptcha}
              text={t('Place Order')}
              type='submit'
            />
          </div>
        </div>
      </CreditCardForm.Foot>
    </CreditCardForm>
  );
};

export default PaymentFormCreditCard;
